import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, institutionsAPI, rolesAPI, authAPI } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  Plus, 
  Pencil, 
  User,
  Users,
  MagnifyingGlass,
  Spinner,
  WarningCircle,
  Shield,
  GraduationCap,
  ChalkboardTeacher,
  Buildings,
  CheckCircle,
  XCircle,
  Clock,
  UserPlus,
  Key,
  Eye,
  EyeSlash,
  Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', icon: Shield, color: 'bg-red-100 text-red-700' },
  admin: { label: 'Administrador', icon: Shield, color: 'bg-purple-100 text-purple-700' },
  instructor: { label: 'Instructor', icon: ChalkboardTeacher, color: 'bg-blue-100 text-blue-700' },
  alumno: { label: 'Alumno', icon: GraduationCap, color: 'bg-green-100 text-green-700' },
};

const STATUS_CONFIG = {
  activo: { label: 'Activo', icon: CheckCircle, color: 'bg-green-100 text-green-700' },
  inactivo: { label: 'Inactivo', icon: XCircle, color: 'bg-slate-100 text-slate-600' },
  pendiente: { label: 'Pendiente', icon: Clock, color: 'bg-yellow-100 text-yellow-700' },
};

export default function UsersPage() {
  const { user: currentUser, isSuperAdmin, isAdmin, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedInstitution, setSelectedInstitution] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Password Reset Modal
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  const canEdit = isSuperAdmin || hasPermission('usuarios', 'editar');
  const canCreate = isSuperAdmin || hasPermission('usuarios', 'crear');
  const canResetPassword = isSuperAdmin || isAdmin;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, instsRes] = await Promise.all([
        usersAPI.getAll(),
        institutionsAPI.getAll()
      ]);
      setUsers(usersRes.data);
      setInstitutions(instsRes.data);
      
      // Try to load custom roles
      try {
        const rolesRes = await rolesAPI.getAll();
        setRoles(rolesRes.data);
      } catch {
        setRoles([]);
      }
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user) => {
    setSelectedUser(user);
    setFormData({
      role: user.role || 'alumno',
      institution_id: user.institution_id || '',
      status: user.status || 'activo'
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.institution_id && formData.status === 'activo') {
      toast.error('Debe asignar un establecimiento para activar el usuario');
      return;
    }

    setSaving(true);
    try {
      await usersAPI.update(selectedUser.id, formData);
      toast.success('Usuario actualizado');
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAction = async (user, action) => {
    try {
      switch (action) {
        case 'activate':
          if (!user.institution_id) {
            toast.error('Asigne un establecimiento primero');
            handleOpenModal(user);
            return;
          }
          await usersAPI.update(user.id, { status: 'activo' });
          toast.success('Usuario activado');
          break;
        case 'deactivate':
          await usersAPI.update(user.id, { status: 'inactivo' });
          toast.success('Usuario desactivado');
          break;
      }
      loadData();
    } catch (error) {
      toast.error('Error al realizar acción');
    }
  };

  // Reset Password Functions
  const handleOpenPasswordModal = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setShowPassword(false);
    setShowPasswordModal(true);
  };

  const handleResetPassword = async () => {
    if (!passwordUser) return;
    
    // Validaciones
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    try {
      setResettingPassword(true);
      await authAPI.adminResetPassword(passwordUser.id, newPassword);
      toast.success(`Contraseña de ${passwordUser.email} actualizada exitosamente`);
      setShowPasswordModal(false);
      setPasswordUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al restablecer contraseña';
      toast.error(errorMsg);
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.apellido?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === 'all' || u.role === selectedRole;
    const matchesStatus = selectedStatus === 'all' || u.status === selectedStatus;
    const matchesInstitution = selectedInstitution === 'all' || 
      (selectedInstitution === 'unassigned' ? !u.institution_id : u.institution_id === selectedInstitution);
    return matchesSearch && matchesRole && matchesStatus && matchesInstitution;
  });

  const getRoleBadge = (role) => {
    const config = ROLE_CONFIG[role] || ROLE_CONFIG.alumno;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon size={12} weight="bold" />
        {config.label}
      </Badge>
    );
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.activo;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon size={12} weight="fill" />
        {config.label}
      </Badge>
    );
  };

  // Stats
  const stats = {
    total: users.length,
    activos: users.filter(u => u.status === 'activo' || (!u.status && u.is_active)).length,
    pendientes: users.filter(u => u.status === 'pendiente').length,
    sinEstablecimiento: users.filter(u => !u.institution_id).length,
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center h-64">
          <Spinner size={48} className="animate-spin text-blue-600" />
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-['Chivo'] text-slate-900" data-testid="users-title">
              Gestión de Usuarios
            </h1>
            <p className="text-slate-500">
              Administra usuarios, roles y asignaciones de establecimiento
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <Users size={24} className="mx-auto text-slate-400 mb-2" />
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              <p className="text-xs text-slate-500">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <CheckCircle size={24} className="mx-auto text-green-500 mb-2" />
              <p className="text-2xl font-bold text-green-600">{stats.activos}</p>
              <p className="text-xs text-slate-500">Activos</p>
            </CardContent>
          </Card>
          <Card className={stats.pendientes > 0 ? 'border-yellow-300 bg-yellow-50' : ''}>
            <CardContent className="pt-4 text-center">
              <Clock size={24} className={`mx-auto mb-2 ${stats.pendientes > 0 ? 'text-yellow-600' : 'text-slate-400'}`} />
              <p className={`text-2xl font-bold ${stats.pendientes > 0 ? 'text-yellow-600' : 'text-slate-600'}`}>{stats.pendientes}</p>
              <p className="text-xs text-slate-500">Pendientes</p>
            </CardContent>
          </Card>
          <Card className={stats.sinEstablecimiento > 0 ? 'border-red-300 bg-red-50' : ''}>
            <CardContent className="pt-4 text-center">
              <Buildings size={24} className={`mx-auto mb-2 ${stats.sinEstablecimiento > 0 ? 'text-red-500' : 'text-slate-400'}`} />
              <p className={`text-2xl font-bold ${stats.sinEstablecimiento > 0 ? 'text-red-600' : 'text-slate-600'}`}>{stats.sinEstablecimiento}</p>
              <p className="text-xs text-slate-500">Sin Establecimiento</p>
            </CardContent>
          </Card>
        </div>

        {/* Pending Users Alert */}
        {stats.pendientes > 0 && (
          <Card className="border-yellow-300 bg-yellow-50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock size={24} className="text-yellow-600" />
                <div>
                  <p className="font-medium text-yellow-800">
                    {stats.pendientes} usuario(s) pendiente(s) de activación
                  </p>
                  <p className="text-sm text-yellow-700">
                    Asigne establecimiento y rol para activarlos
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar usuarios..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className="w-full lg:w-40" data-testid="role-filter">
                  <SelectValue placeholder="Rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los roles</SelectItem>
                  {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="w-full lg:w-40" data-testid="status-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedInstitution} onValueChange={setSelectedInstitution}>
                <SelectTrigger className="w-full lg:w-48" data-testid="institution-filter">
                  <SelectValue placeholder="Establecimiento" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="unassigned">Sin asignar</SelectItem>
                  {institutions.map(inst => (
                    <SelectItem key={inst.id} value={inst.id}>{inst.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Usuario</th>
                    <th className="text-left p-4 font-medium text-slate-600">Email</th>
                    <th className="text-left p-4 font-medium text-slate-600">Establecimiento</th>
                    <th className="text-left p-4 font-medium text-slate-600">Rol</th>
                    <th className="text-left p-4 font-medium text-slate-600">Estado</th>
                    <th className="text-right p-4 font-medium text-slate-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredUsers.map(user => (
                    <tr key={user.id} className={`hover:bg-slate-50 ${user.status === 'pendiente' ? 'bg-yellow-50/50' : ''}`} data-testid={`user-row-${user.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            user.status === 'pendiente' ? 'bg-yellow-100' : 'bg-blue-100'
                          }`}>
                            <User size={20} className={user.status === 'pendiente' ? 'text-yellow-600' : 'text-blue-600'} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{user.nombre} {user.apellido}</p>
                            <p className="text-xs text-slate-500">{user.rut || 'Sin RUT'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-slate-600">{user.email}</td>
                      <td className="p-4">
                        {user.institution_id ? (
                          <div className="flex items-center gap-2">
                            <Buildings size={16} className="text-slate-400" />
                            <span className="text-slate-600">{user.institution_name || 'Asignado'}</span>
                          </div>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">Sin asignar</Badge>
                        )}
                      </td>
                      <td className="p-4">{getRoleBadge(user.role)}</td>
                      <td className="p-4">{getStatusBadge(user.status || 'activo')}</td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          {canEdit && user.id !== currentUser?.id && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleOpenModal(user)}
                                data-testid={`edit-user-${user.id}`}
                                title="Editar usuario"
                              >
                                <Pencil size={16} />
                              </Button>
                              {canResetPassword && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-orange-600 border-orange-300 hover:bg-orange-50"
                                  onClick={() => handleOpenPasswordModal(user)}
                                  data-testid={`reset-password-${user.id}`}
                                  title="Restablecer contraseña"
                                >
                                  <Key size={16} />
                                </Button>
                              )}
                              {user.status === 'pendiente' && user.institution_id && (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className="text-green-600 border-green-300 hover:bg-green-50"
                                  onClick={() => handleQuickAction(user, 'activate')}
                                  data-testid={`activate-user-${user.id}`}
                                  title="Activar usuario"
                                >
                                  <CheckCircle size={16} />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {filteredUsers.length === 0 && (
          <Card className="p-8 text-center">
            <WarningCircle size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No se encontraron usuarios</p>
          </Card>
        )}

        {/* Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Usuario</DialogTitle>
              <DialogDescription>
                Asigna establecimiento, rol y estado al usuario
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {/* User Info */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <User size={24} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">{selectedUser?.nombre} {selectedUser?.apellido}</p>
                  <p className="text-sm text-slate-500">{selectedUser?.email}</p>
                </div>
              </div>

              {/* Institution Select */}
              <div className="space-y-2">
                <Label>Establecimiento *</Label>
                <Select 
                  value={formData.institution_id || ''} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, institution_id: v }))}
                >
                  <SelectTrigger data-testid="user-institution-select">
                    <SelectValue placeholder="Seleccionar establecimiento" />
                  </SelectTrigger>
                  <SelectContent>
                    {institutions.map(inst => (
                      <SelectItem key={inst.id} value={inst.id}>
                        <div className="flex items-center gap-2">
                          <Buildings size={16} />
                          {inst.nombre}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!formData.institution_id && (
                  <p className="text-xs text-red-500">
                    * Obligatorio para activar el usuario
                  </p>
                )}
              </div>

              {/* Role Select */}
              <div className="space-y-2">
                <Label>Rol</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, role: v }))}
                >
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_CONFIG).map(([key, config]) => {
                      // Only super_admin can assign super_admin role
                      if (key === 'super_admin' && !isSuperAdmin) return null;
                      const Icon = config.icon;
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon size={16} />
                            {config.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Status Select */}
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger data-testid="user-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <Icon size={16} />
                            {config.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn">
                {saving ? <Spinner size={18} className="animate-spin" /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal Restablecer Contraseña */}
        <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key size={24} className="text-orange-500" />
                Restablecer Contraseña
              </DialogTitle>
              <DialogDescription>
                Establecer una nueva contraseña para <strong>{passwordUser?.email}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
                <Warning size={20} className="text-orange-500 shrink-0 mt-0.5" />
                <p className="text-sm text-orange-700">
                  La nueva contraseña reemplazará la actual del usuario. Asegúrese de comunicarle la nueva contraseña de forma segura.
                </p>
              </div>
              
              <div>
                <Label htmlFor="new-password">Nueva Contraseña *</Label>
                <div className="relative mt-1">
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    className="pr-10"
                    data-testid="new-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </Button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="confirm-password">Confirmar Contraseña *</Label>
                <Input
                  id="confirm-password"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repetir contraseña"
                  className="mt-1"
                  data-testid="confirm-password-input"
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-sm text-red-500 mt-1">Las contraseñas no coinciden</p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPasswordModal(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleResetPassword} 
                disabled={resettingPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="confirm-reset-password-btn"
              >
                {resettingPassword ? (
                  <Spinner size={18} className="animate-spin" />
                ) : (
                  <>
                    <Key size={16} className="mr-2" />
                    Restablecer Contraseña
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
